import api from './api';

// API client for the "Broker Management" screen — per-(broker, feature) enable/disable +
// credentials for every broker besides Dhan (Dhan's own credentials stay on the Settings page /
// tokenAPI, unchanged).

export type BrokerName = 'DHAN' | 'GROWW' | 'ANGEL_ONE' | 'FYERS' | 'ALICE_BLUE' | 'KOTAK_NEO' | 'UPSTOX' | 'ZERODHA';
export const ALL_BROKERS: BrokerName[] = ['DHAN', 'GROWW', 'ANGEL_ONE', 'FYERS', 'ALICE_BLUE', 'KOTAK_NEO', 'UPSTOX', 'ZERODHA'];
export const BROKER_LABELS: Record<BrokerName, string> = {
  DHAN: 'Dhan', GROWW: 'Groww', ANGEL_ONE: 'Angel One', FYERS: 'FYERS', ALICE_BLUE: 'Alice Blue', KOTAK_NEO: 'Kotak Neo',
  UPSTOX: 'Upstox', ZERODHA: 'Zerodha',
};
// react-bootstrap Badge "bg" variants — one distinct color per broker for the Orders pages.
export const BROKER_BADGE_VARIANT: Record<BrokerName, string> = {
  DHAN: 'primary', GROWW: 'success', ANGEL_ONE: 'info', FYERS: 'warning', ALICE_BLUE: 'dark', KOTAK_NEO: 'danger',
  UPSTOX: 'secondary', ZERODHA: 'light',
};
// Confirmed live: ZERODHA's 'light' bg badge rendered with the default (white) badge text, making
// it unreadable — the only "light background" variant among otherwise all-dark ones. Every Orders
// page passes this as the Badge's `text` prop alongside BROKER_BADGE_VARIANT's `bg`.
export const BROKER_BADGE_TEXT: Partial<Record<BrokerName, string>> = {
  ZERODHA: 'dark',
};
// NIFTY_SPREAD/SENSEX_SPREAD and NIFTY_BUYING/SENSEX_BUYING were each one combined switch before —
// client's explicit ask: split both so a broker can be enabled for one market's auto-Spread/Buying
// without the other, same independence NSE/BSE already had. MANUAL_SPREAD stays combined.
export type BrokerFeature = 'NSE' | 'BSE' | 'NIFTY_SPREAD' | 'SENSEX_SPREAD' | 'MANUAL_SPREAD' | 'OPTION_SELLING' | 'CASH_MARKET' | 'NIFTY_BUYING' | 'SENSEX_BUYING';
export const ALL_BROKER_FEATURES: BrokerFeature[] = ['NSE', 'BSE', 'NIFTY_SPREAD', 'SENSEX_SPREAD', 'MANUAL_SPREAD', 'OPTION_SELLING', 'CASH_MARKET', 'NIFTY_BUYING', 'SENSEX_BUYING'];
export const FEATURE_LABELS: Record<BrokerFeature, string> = {
  NSE: 'NSE', BSE: 'BSE', NIFTY_SPREAD: 'NY Spread', SENSEX_SPREAD: 'SX Spread', MANUAL_SPREAD: 'Manual Spread',
  OPTION_SELLING: 'Option Selling', CASH_MARKET: 'Cash Market', NIFTY_BUYING: 'Nifty Buying', SENSEX_BUYING: 'Sensex Buying',
};

export interface BrokerStatus {
  broker: BrokerName;
  implemented: boolean;
  credentials: {
    api_key?: string; client_code?: string; hasPin?: boolean; hasToTpSecret?: boolean;
    client_id?: string; redirect_uri?: string; hasSecretKey?: boolean;
    app_code?: string; hasApiSecret?: boolean; connected?: boolean; sessionIssuedDate?: string | null;
    ucc?: string; mobile_number?: string; hasMpin?: boolean; hasConsumerKey?: boolean;
    hasTotpSecret?: boolean;
  };
}

export interface BrokerFeatureToggle {
  broker: BrokerName;
  feature: BrokerFeature;
  is_enabled: boolean;
}

export interface BrokerCapital {
  broker: BrokerName;
  capital_amount: number;
}

export const brokerAPI = {
  list: () => api.get<BrokerStatus[]>('/broker'),
  // Field set varies per broker's own auth model (2 for Alice Blue, up to 6 for FYERS) — kept as
  // a plain string map here rather than one fixed shape, validated server-side per broker.
  saveCredentials: (broker: BrokerName, data: Record<string, string>) =>
    api.put(`/broker/${broker}/credentials`, data),
  listFeatures: () => api.get<BrokerFeatureToggle[]>('/broker/features'),
  setFeatureEnabled: (broker: BrokerName, feature: BrokerFeature, is_enabled: boolean) =>
    api.put(`/broker/${broker}/feature/${feature}`, { is_enabled }),
  testConnection: (broker: BrokerName) => api.post(`/broker/${broker}/test`),
  // Alice Blue's A3 app needs a real interactive browser login (no TOTP/PIN headless path) — this
  // returns the URL to open in a popup; the backend's /api/alice-blue/callback finishes the rest.
  getAliceBlueLoginUrl: () => api.get<{ loginUrl: string }>('/broker/alice-blue/login-url'),
  // Upstox and Zerodha need the same interactive-popup login as Alice Blue (see
  // upstoxTokenService.ts/zerodhaTokenService.ts for why neither can be automated headlessly).
  getUpstoxLoginUrl: () => api.get<{ loginUrl: string }>('/broker/upstox/login-url'),
  getZerodhaLoginUrl: () => api.get<{ loginUrl: string }>('/broker/zerodha/login-url'),
  // FYERS now needs the same interactive-popup login as Upstox/Zerodha/Alice Blue above — see
  // fyersTokenService.ts for why the previous headless TOTP-simulated login stopped working.
  getFyersLoginUrl: () => api.get<{ loginUrl: string }>('/broker/fyers/login-url'),
  // Manually configured Capital Amount per broker — Nifty/Sensex Buying's % Cap is a percentage
  // of this (not the live balance), so a multi-strike split lands on a predictable total.
  listCapital: () => api.get<BrokerCapital[]>('/broker/capital'),
  setCapital: (broker: BrokerName, capital_amount: number) =>
    api.put(`/broker/${broker}/capital`, { capital_amount }),
};
